//mui
import UploadFile from '@mui/icons-material/UploadFile';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';

import { Box, Button, Stack, Typography, useTheme, Link as MUILink } from '@mui/material';
//constants
import { useAppSelector } from 'hooks/redux.hooks';
import { FormButton } from 'theme/elements/Buttons';
//import ErrorBox from './ErrorBox';
import { acceptableFileFormats, fileExtensionError, fileTooBigError, maxFileSize } from 'constants/global.constants';

import { field } from '../types';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Paragraph1 } from 'theme/elements/Paragraphs';
import { Download } from '@mui/icons-material';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { ErrorLabel } from 'theme/elements/Inputs';
import { validateFileExtensions } from 'utils/index';

type props = {
	valid: boolean;
	cField: field;
	initialValidation: any;
	handleChange: any;
	translation?: any;
};

export function FileUpload(props: props) {
	const { value } = props.cField.props;

	return (
		<Stack direction={'row'} justifyContent={'space-between'}>
			<Box>File</Box>
			{_.isObject(value) && (
				<Link to={_.get(value, 'path')} target="_blank" download>
					Download
				</Link>
			)}
		</Stack>
	);
}

export function FileUploadBox(props: props) {
	const theme = useTheme();
	const [fileName, setFileName] = useState<string | undefined>();
	const [showErrorBox, setShowErrorBox] = useState(false);
	const isFileUploaded = true;
	const [isNewFileUploaded, setIsNewFileUploaded] = useState(false);
	const [errorText, setErrorText] = useState<string | null>(null);
	useEffect(() => {
		const val = props.cField?.props?.value || null;
		if (_.isObject(val) && (val?.originalFileName || val?.name)) {
			setFileName(_.get(val, 'originalFileName') || val?.name);
		} else {
			if (_.isEmpty(val)) {
				setFileName('');
			}
		}
	}, [props.cField.props]);
	const t = useAppSelector(selectTranslations);

	const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		setShowErrorBox(false);
		const files = event.target.files;
		if (files && files.length > 0) {
			const file: File = files[0];
			if (validateFileExtensions(file.name)) {
				if (file.size <= maxFileSize) {
					setFileName(file.name);
					setIsNewFileUploaded(true);
					props.handleChange({ file });
				} else {
					setFileName(undefined);
					setErrorText(fileTooBigError);
					props.handleChange(null);
				}
			} else {
				setFileName(undefined);
				setErrorText(fileExtensionError);
				setShowErrorBox(true);
			}
		}
	};

	const handleDownload = () => {
		if (props?.cField.props?.onClick && _.isObject(props.cField.props?.value)) {
			props?.cField.props?.onClick(props.cField.props?.value);
		}
	};

	const onDownloadClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault(); // Prevent default link navigation to avoid adding '#' to URL
		handleDownload();
	};

	const onDownloadIconClick = () => {
		handleDownload();
	};

	return (
		<Stack gap={1}>
			{props.cField.props.label && props.cField.props.label}
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				sx={{
					backgroundColor: theme.palette.root.brandSecondary_012,
					padding: '32px',
					borderRadius: '8px',
				}}
			>
				{!fileName ? (
					<Paragraph1
						title={props.translation('label.noFileSelected')}
						text={`${t('label.uploadFile')} ${acceptableFileFormats.replaceAll('.', ' ')}`}
					/>
				) : isNewFileUploaded ? (
					<Typography sx={{ overflowWrap: 'break-word' }}>{fileName}</Typography>
				) : (
					<MUILink href="#" sx={{ overflowWrap: 'break-word' }} onClick={onDownloadClick}>
						{decodeURI(fileName)}
					</MUILink>
				)}
				<FormButton>
					{!props.cField.props.disabled ? (
						<Button
							variant="outlined"
							sx={{
								'&.MuiButton-outlined': {
									borderColor: theme.palette.root.basicDeepGreen,
								},
							}}
							component="label"
							startIcon={isFileUploaded ? <CheckCircleOutlineRounded /> : <UploadFile />}
						>
							{props.translation('label.browse')}
							<input type="file" onChange={handleFileUpload} hidden accept={acceptableFileFormats} />
						</Button>
					) : (
						<Download onClick={onDownloadIconClick} />
					)}
				</FormButton>
			</Stack>
			{showErrorBox && errorText && <ErrorLabel>{t(errorText)}</ErrorLabel>}
		</Stack>
	);
}
